import CustomDropdown from '@/components/CommonComponents/CustomDropdown';
import { desanitizeKey } from '@/helper/utils';
import { StepValidatorRef, useStepValidation } from '@/hooks/useStepValidation';
import { forwardRef, useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { surveysetCreationType } from './CreateSurveySet';

type NotificationSettingsProps = {
  isEditing?: boolean;
};

const NotificationSettings = forwardRef<StepValidatorRef, NotificationSettingsProps>(
  ({ isEditing }, ref) => {
    useStepValidation(ref, [
      'greetingMsg',
      'consentMsg',
      'rewardMsg',
      'participantLimit',
      'submissionRule',
      'maxParticipants',
    ]);

    const isInitializing = useRef(true);
    const {
      getValues,
      watch,
      control,
      register,
      formState: { errors },
      setValue,
    } = useFormContext<surveysetCreationType>();

    const submissionRuleOptions = ['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY', 'NO_LIMIT'];

    const participantLimit = useWatch({
      control,
      name: 'participantLimit',
    });
    useEffect(() => {
      if (participantLimit === false) {
        setValue('maxParticipants', undefined);
      }
    }, [participantLimit, setValue]);
    return (
      <div className="mx-auto text-[#621C43]">
        <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
          <h2 className="text-xl font-bold">
            {isEditing ? 'Edit Notification Settings' : 'Notification Settings'}
          </h2>
          <p className="text-base font-medium">
            {isEditing
              ? 'Configure user messages and participation limits'
              : 'Configure user messages and participation limits'}
          </p>
        </div>

        <div className="px-6 pt-6 space-y-6">
          {/* Greeting Message */}
          <div className="">
            <label className="block text-sm font-medium text-[#883c4a] mb-2">
              Greeting Message<span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full p-2 rounded-lg border bg-[#4A082D33] focus:outline-none text-[#621C43]"
              rows={2}
              {...register('greetingMsg', {
                required: 'Greeting message is required',
              })}
              placeholder="Enter greeting message"
            />
            <p className="text-xs">
              This message will be displayed to users when they start the survey.
            </p>
          </div>
          {/* Consent Message */}
          <div className="">
            <label className="block text-sm font-medium text-[#883c4a] mb-2">
              Consent Message<span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full p-2 rounded-lg border bg-[#4A082D33] focus:outline-none text-[#621C43]"
              rows={3}
              {...register('consentMsg', {
                required: 'Consent message is required',
              })}
              placeholder="Enter consent message"
            />
            <p className="text-xs">Required consent message for participants to continue.</p>
          </div>
          {/* Submission Rule */}
          <div>
            <label className="block text-sm font-medium text-[#883c4a] mb-2">
              Submission Rule<span className="text-red-500">*</span>
            </label>
            <Controller
              name="submissionRule"
              control={control}
              rules={{
                required: 'Submission Rule is required',
                validate: (value) =>
                  value && submissionRuleOptions.includes(value)
                    ? true
                    : 'Please select a valid submission rule',
              }}
              render={({ field }) => (
                <CustomDropdown
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  options={[
                    { value: '', label: 'Select Submission Rule' },
                    ...submissionRuleOptions.map((s_rule) => ({
                      value: s_rule,
                      label: desanitizeKey(s_rule),
                    })),
                  ]}
                  placeholder="Select Submission "
                  className="bg-[#4A082D33]/10 rounded-lg text-base placeholder:text-base"
                />
              )}
            />

            {errors.submissionRule && (
              <p className="text-red-500 text-xs mt-1">{errors.submissionRule.message}</p>
            )}

            <p className="text-xs mt-1">
              Controls how frequently a participant can submit the survey.
            </p>
          </div>
          <div className="">
            <label className="block text-sm font-medium text-[#883c4a] mb-3">
              Participant Limit
            </label>
            <div className="flex gap-6 mb-3">
              <Controller
                name="participantLimit"
                control={control}
                defaultValue={null} // no default
                rules={{
                  validate: (value) =>
                    value === true || value === false || 'Please select participant limit',
                }}
                render={({ field, fieldState }) => (
                  <>
                    <div className="flex gap-6 mb-2">
                      {/* YES */}
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                          className="hidden"
                        />
                        <div className="flex items-center justify-center size-5 rounded-full border border-[#621C43]">
                          {field.value === true && (
                            <div className="size-3 rounded-full bg-[#621C43]" />
                          )}
                        </div>
                        Yes
                      </label>

                      {/* NO */}
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          checked={field.value === false}
                          onChange={() => field.onChange(false)}
                          className="hidden"
                        />
                        <div className="flex items-center justify-center size-5 rounded-full border border-[#621C43]">
                          {field.value === false && (
                            <div className="size-3 rounded-full bg-[#621C43]" />
                          )}
                        </div>
                        No
                      </label>
                    </div>

                    {/* Error Message */}
                    {fieldState.error && (
                      <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </div>

            {participantLimit && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-[#883c4a] mb-2">
                    Maximum Participants<span className="text-red-500">*</span>
                  </label>

                  <input
                    type="number"
                    {...register('maxParticipants', {
                      required: 'Maximum number of participants is required',
                      valueAsNumber: true,
                      min: {
                        value: 1,
                        message: 'Maximum participants must be greater than 0',
                      },
                      validate: {
                        isInteger: (value) =>
                          Number.isInteger(value) || 'Only whole numbers are allowed',

                        greaterThanMin: (value) => {
                          return (
                            value! > 0 ||
                            'Maximum participants must be greater than minimum participants'
                          );
                        },
                      },
                    })}
                    className={`w-full p-2 rounded-lg border focus:outline-none text-[#621C43] ${
                      errors.maxParticipants
                        ? 'border-red-500 bg-red-50'
                        : 'bg-[#4A082D33] border-gray-300'
                    }`}
                    placeholder="Enter maximum participants"
                  />

                  {errors.maxParticipants && (
                    <p className="text-xs text-red-500 mt-1">{errors.maxParticipants.message}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default NotificationSettings;
